<template>
  <div class="max-w-screen-sm mx-auto">
    <h1 class="font-heading font-semibold text-4xl mb-3">Automatic Sales Reporting</h1>
    <p class="text-fg-secondary mb-8">Send a sales report each Monday morning.</p>
    <ChipInput
      :initialValue="initialEmailRecipients"
      v-model="emailRecipients"
      label="Email List"
      placeholder="Enter email addresses"
      :validationFn="validateEmail"
      invalidMessage="Please enter a valid email address"
      duplicateMessage="This email is already added to the list"
      inputId="emailRecipientsInput"
    >
      <template #helperText>
        Press Enter, Tab or comma to add •
        <span
          v-if="emailRecipients.length > 0"
          class="inline-flex items-baseline gap-1 text-status-seated"
        >
          {{ emailRecipients.length }} recipient{{ emailRecipients.length !== 1 ? 's' : '' }}
        </span>
      </template>
    </ChipInput>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted } from 'vue'
import { z } from 'zod'

import ChipInput from '@/common/components/molecules/ChipInput.vue'
import { useReportStore } from '@/modules/report/stores/ReportStore'
import { useReportUpdateStore } from '@/modules/report/stores/ReportUpdateStore'

const reportUpdateStore = useReportUpdateStore()
const { updatedReportSchedules } = storeToRefs(reportUpdateStore)
const reportStore = useReportStore()
const { initialEmailRecipients } = storeToRefs(reportStore)
const currentSchedule = computed(() => {
  return updatedReportSchedules.value.length > 0 ? updatedReportSchedules.value[0] : null
})
const emailRecipients = computed<string[]>({
  get: () => currentSchedule.value?.recipients || [],
  set: (newRecipients: string[]) => {
    if (currentSchedule.value) {
      // Update the specific schedule in the update store
      reportUpdateStore.updateReportSchedule({
        id: currentSchedule.value.id,
        recipients: newRecipients,
      })
    }
  },
})
onMounted(async () => {
  if (updatedReportSchedules.value.length === 0) reportUpdateStore.addReportSchedule()
})

const emailSchema = z.email({ message: 'Please enter a valid email address' })
function validateEmail(email: string): { isValid: boolean; message?: string } {
  const result = emailSchema.safeParse(email)
  if (result.success) return { isValid: true }
  else return { isValid: false, message: result.error.issues[0].message }
}
</script>
